import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import GridTable from '../../table/GridTable';
import ConfirmDialog from '../../modal/ConfirmDialog';
import { ColDef } from 'ag-grid-community';
import {
  useConsultOrderCodeStore,
  IConsultOrderCode,
  IConsultOrderCodeSearch,
} from '../../../store/useConsultOrderCode';
import { useLoginStore } from '../../../store/useLogin';
import CodeModal from './CodeModal';
import ConsultManagementFilter from './filter/ConsultManagementFilter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCode, faLink, faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { preventWheelChange } from '../../../util/numberInput';

const SHARE_LINK_BASE =
  process.env.REACT_APP_CONSULT_SHARE_LINK_BASE ??
  'https://fortunelab.coredev.co.kr/consult';

const SCRIPT_URL =
    process.env.REACT_APP_EMBED_SCRIPT_URL ??
    'https://saju.coredev.co.kr/saju-embed.js';

const BUTTON_SCRIPT_URL =
  process.env.REACT_APP_EMBED_BUTTON_SCRIPT_URL ??
  'https://saju.coredev.co.kr/saju-embed-button.js';

const ConsultManagementPage = () => {
  const { codeList, codePage, fetchCodes, setSearch, deleteCodes, loading } =
    useConsultOrderCodeStore();
  const { role } = useLoginStore();
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState<IConsultOrderCode | null>(null);

  // 일괄 삭제용 그리드 ref / 모달 상태
  const gridRef = useRef<AgGridReact<IConsultOrderCode>>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);

  // 임베드 코드 모달을 위한 상태
  const [isEmbedModalOpen, setEmbedModalOpen] = useState(false);
  const [embedCodeToCopy, setEmbedCodeToCopy] = useState('');

  const rowSelection = useMemo(() => ({ mode: 'multiRow' as const }), []);

  const handleSelectionChanged = useCallback(() => {
    setSelectedCount(gridRef.current?.api.getSelectedRows().length ?? 0);
  }, []);

  const showDeleteModal = useCallback(() => {
    const selectedRows = gridRef.current?.api.getSelectedRows() ?? [];
    if (selectedRows.length === 0) {
      toast.warn('선택된 캠페인이 없습니다.');
      return;
    }
    setIsDeleteOpen(true);
  }, []);

  const onDeleteConfirm = useCallback(async () => {
    const selectedRows = gridRef.current?.api.getSelectedRows() ?? [];
    const ids = selectedRows.map(row => row.id).filter((id): id is number => typeof id === 'number');
    if (ids.length === 0) {
      toast.warn('선택된 캠페인이 없습니다.');
      setIsDeleteOpen(false);
      return;
    }
    const success = await deleteCodes(ids);
    setIsDeleteOpen(false);
    if (success) {
      gridRef.current?.api.deselectAll();
      setSelectedCount(0);
    }
  }, [deleteCodes]);

  const handleSearch = useCallback(
    (search: IConsultOrderCodeSearch) => {
      setSearch(search);
      fetchCodes(0);
    },
    [setSearch, fetchCodes]
  );

  const handleResetSearch = useCallback(() => {
    setSearch({});
    fetchCodes(0);
  }, [setSearch, fetchCodes]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handlePagination = useCallback(
    (page: number) => {
      fetchCodes(page);
    },
    [fetchCodes]
  );

  const copyShareLink = useCallback((trkId: string) => {
    const shareLink = `${SHARE_LINK_BASE}${trkId}`;
    navigator.clipboard.writeText(shareLink).then(
      () => toast.success('공유 링크가 복사되었습니다.'),
      () => toast.error('복사에 실패했습니다.'),
    );
  }, []);

  const [embedType, setEmbedType] = useState<'landing' | 'button'>('landing');
  const [landingWidth, setLandingWidth] = useState('600');
  const [landingOnlyForm, setLandingOnlyForm] = useState(false);
  const [buttonWidth, setButtonWidth] = useState('600');
  const [buttonModalPosition, setButtonModalPosition] = useState<'left' | 'center' | 'right'>('center');
  const [buttonTypingEnabled, setButtonTypingEnabled] = useState(true);

  const buildLandingEmbedCode = useCallback(
    (trkId: string, width: string, onlyForm: boolean) =>
      `<saju-embed landing="consult/${trkId}" width="${width}"${onlyForm ? ' onlyform="1"' : ''}></saju-embed>\n<script src="${SCRIPT_URL}"></script>`,
    [SCRIPT_URL]
  );

  const buildButtonEmbedCode = useCallback(
    (trkId: string, width: string, modalPosition: 'left' | 'center' | 'right', typingEnabled: boolean) => {
      const typingAttribute = typingEnabled ? '' : ' typing-enabled="0"';
      return `<saju-embed-button landing="consult/${trkId}" width="${width}" modal-position="${modalPosition}"${typingAttribute}></saju-embed-button>\n<script src="${BUTTON_SCRIPT_URL}"></script>`;
    },
    [BUTTON_SCRIPT_URL]
  );

  const resetEmbedModalState = useCallback(() => {
    setEmbedModalOpen(false);
    setEmbedCodeToCopy('');
    setEmbedType('landing');
    setLandingWidth('600');
    setLandingOnlyForm(false);
    setButtonWidth('600');
    setButtonModalPosition('center');
    setButtonTypingEnabled(true);
    setSelectedCode(null);
  }, []);

  const handleOpenEmbedModal = useCallback((code: IConsultOrderCode) => {
    setSelectedCode(code);
    setEmbedType('landing');
    setLandingWidth('600');
    setLandingOnlyForm(false);
    setButtonWidth('600');
    setButtonModalPosition('center');
    setButtonTypingEnabled(true);
    setEmbedCodeToCopy('');
    setEmbedModalOpen(true);
  }, []);

  const handleCopyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(embedCodeToCopy).then(
      () => {
        toast.success('임베드 코드가 복사되었습니다.');
        resetEmbedModalState();
      },
      () => toast.error('복사에 실패했습니다.'),
    );
  }, [embedCodeToCopy, resetEmbedModalState]);

  const columnDefs = useMemo<ColDef<IConsultOrderCode>[]>(
    () => {
      const baseColumns: ColDef<IConsultOrderCode>[] = [
        {
          headerName: '캠페인명',
          field: 'campaignName',
          flex: 2,
          valueFormatter: params => params.value ?? '-',
        },
        {
          headerName: '공유 링크',
          flex: 3,
          cellRenderer: (params: any) => {
            const trkId = params.data?.trkId;
            if (!trkId) return null;
            const link = `${SHARE_LINK_BASE}${trkId}`;
            return (
              <div className="!flex !items-center !gap-2 !text-sm !h-full !w-full" style={{ minWidth: 0 }}>
                <span className="!flex-1 !truncate" title={link} style={{ minWidth: 0 }}>
                  {link}
                </span>
                <button
                  className="!text-blue-600 hover:!text-blue-800"
                  onClick={() => copyShareLink(trkId)}
                  title="공유 링크 복사"
                >
                  <FontAwesomeIcon icon={faLink} />
                </button>
              </div>
            );
          },
        },
      ];

      if (role === 'ROLE_ADMIN') {
        baseColumns.push({
          headerName: '가맹점 명',
          field: 'franchiseName',
          flex: 1.8,
          valueFormatter: params => params.value ?? '-',
        });
      }

      baseColumns.push(
        {
          headerName: '방문 수',
          field: 'landedUserCount',
          flex: 1,
          valueFormatter: params => params.value?.toLocaleString(),
        },
        {
          headerName: '결과 조회 수',
          field: 'resultUserCount',
          flex: 1,
          valueFormatter: params => params.value?.toLocaleString(),
        },
        {
          headerName: '전환율',
          flex: 1.1,
          valueFormatter: params => {
            const landed = params.data?.landedUserCount ?? 0;
            const result = params.data?.resultUserCount ?? 0;
            if (!landed) {
              return '-';
            }
            const rate = (result / landed) * 100;
            return `${rate.toFixed(1)}%`;
          },
        },
        {
          headerName: '생성일',
          field: 'createdAt',
          flex: 1.5,
          valueFormatter: params => (params.value ? new Date(params.value).toLocaleString() : ''),
        },
        {
          headerName: '입력폼',
          width: 80,
          cellRenderer: (params: any) => {
            const code: IConsultOrderCode | undefined = params.data;
            if (!code?.trkId) return null;
            return (
              <button
                className="!text-blue-600 hover:!text-blue-800"
                onClick={() => handleOpenEmbedModal(code)}
                title="입력폼 퍼가기"
              >
                <FontAwesomeIcon icon={faCode} />
              </button>
            );
          },
        },
        {
          headerName: '관리',
          width: 80,
          cellRenderer: (params: any) => {
            const code: IConsultOrderCode | undefined = params.data;
            if (!code) return null;
            return (
              <button
                className="!text-blue-600 hover:!text-blue-800"
                onClick={() => {
                  setSelectedCode(code);
                  setEditModalOpen(true);
                }}
                title="캠페인 수정"
              >
                <FontAwesomeIcon icon={faPen} />
              </button>
            );
          },
        }
      );

      return baseColumns;
    },
    [copyShareLink, handleOpenEmbedModal, role]
  );

  const visiblePageIndexes = useMemo(() => {
    if (!codePage) return [];
    const totalPages = codePage.totalPages;
    const currentPage = codePage.pageable.pageNumber;
    const maxVisible = 10;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);
    startPage = Math.max(0, endPage - maxVisible + 1);

    return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
  }, [codePage]);

	  useEffect(() => {
	    if (!isEmbedModalOpen || !selectedCode) return;

	    const normalizedLandingWidth = (landingWidth || '').trim() || '600';
	    const normalizedButtonWidth = (buttonWidth || '').trim() || '600';

	    if (embedType === 'landing') {
	      setEmbedCodeToCopy(buildLandingEmbedCode(selectedCode.trkId, normalizedLandingWidth, landingOnlyForm));
	    } else {
	      setEmbedCodeToCopy(
	        buildButtonEmbedCode(selectedCode.trkId, normalizedButtonWidth, buttonModalPosition, buttonTypingEnabled)
	      );
	    }
	  }, [
	    isEmbedModalOpen,
	    selectedCode,
	    embedType,
	    landingWidth,
	    landingOnlyForm,
	    buttonWidth,
	    buttonModalPosition,
	    buttonTypingEnabled,
	    buildLandingEmbedCode,
	    buildButtonEmbedCode,
	  ]);

  return (
    <div className="!w-full !h-full !min-w-0 !overflow-hidden !flex !flex-col">
      <div className="!pt-4">
        <ConsultManagementFilter onSearch={handleSearch} onReset={handleResetSearch} />
      </div>
      <div className="!flex !items-center !justify-between !py-4">
        <button
          className="!flex !items-center !gap-2 !px-4 !py-2 !rounded !bg-blue-600 !text-white hover:!bg-blue-700"
          onClick={() => setCreateModalOpen(true)}
        >
          <FontAwesomeIcon icon={faPlus} />
          랜딩페이지 생성
        </button>
        {selectedCount > 0 && (
          <button
            className="!flex !items-center !gap-2 !px-4 !py-2 !rounded !bg-red-600 !text-white hover:!bg-red-700"
            onClick={showDeleteModal}
          >
            <FontAwesomeIcon icon={faTrash} />
            선택 삭제 ({selectedCount}건)
          </button>
        )}
      </div>

      <div className="!flex-1 !min-h-0 !min-w-0 !w-full !overflow-hidden">
        <GridTable<IConsultOrderCode>
          gridRef={gridRef}
          height="100%"
          rowData={codeList}
          columnDefs={columnDefs}
          isLoading={loading}
          rowSelection={rowSelection}
          onSelectionChanged={handleSelectionChanged}
        />
      </div>

      {codePage && codePage.totalPages > 1 && (
        <div className="!w-full !mt-4 !overflow-x-auto !pb-1">
          <div className="!flex !justify-center !items-center !gap-2 !min-w-max">
            {visiblePageIndexes.map(index => (
              <button
                key={index}
                onClick={() => handlePagination(index)}
                className={`!px-3 !py-1 !rounded !text-sm ${
                  index === codePage.pageable.pageNumber
                    ? '!bg-blue-600 !text-white'
                    : 'hover:!bg-gray-100'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 임베드 코드 표시 모달 */}
      {isEmbedModalOpen && (
        <div className="!fixed !inset-0 !bg-black !bg-opacity-50 !flex !items-center !justify-center !z-50">
          <div className="!bg-white !p-6 !rounded-lg !shadow-xl !w-full !max-w-lg">
            <h3 className="!text-lg !font-bold !mb-4">입력폼 코드 퍼가기</h3>
            <p className="!text-sm !mb-4 !text-gray-500">
              입력폼은 홈페이지나 HTML이 사용가능한 블로그/카페 등에 삽입하여 사용가능합니다. 생성하기 후 복사를 통하여 소스 코드를
              복사하고 삽입할 위치에 추가하시면 됩니다.
            </p>
            <div className="!mb-4">
              <div className="!inline-flex !rounded-md !border !overflow-hidden">
                <button
                  type="button"
                  className={`!px-4 !py-2 !text-sm ${
                    embedType === 'landing'
                      ? '!bg-blue-600 !text-white'
                      : '!bg-white !text-gray-600 hover:!bg-gray-50'
                  }`}
                  onClick={() => setEmbedType('landing')}
                >
                  랜딩 버전
                </button>
                <button
                  type="button"
                  className={`!px-4 !py-2 !text-sm ${
                    embedType === 'button'
                      ? '!bg-blue-600 !text-white'
                      : '!bg-white !text-gray-600 hover:!bg-gray-50'
                  }`}
                  onClick={() => setEmbedType('button')}
                >
                  버튼 버전
                </button>
              </div>
            </div>

            {embedType === 'landing' ? (
              <div className="!mb-4 !space-y-4">
                <div>
                  <div className="!flex !items-center !justify-between !mb-2">
                    <label className="!text-sm !font-medium !text-gray-700">가로폭(px)</label>
                  </div>
                  <div className="!flex !items-center !gap-2">
                    <input
                      type="number"
                      min="200"
                      placeholder="예) 600"
                      className="!flex-1 !p-2 !border !rounded"
                      value={landingWidth}
                      onChange={(event) => setLandingWidth(event.target.value)}
                      onWheel={preventWheelChange}
                    />
                  </div>
                </div>

                <div className="!flex !items-center !gap-2">
                  <input
                    id="landingOnlyFormToggle"
                    type="checkbox"
                    checked={landingOnlyForm}
                    onChange={(event) => setLandingOnlyForm(event.target.checked)}
                  />
                  <label htmlFor="landingOnlyFormToggle" className="!text-sm !text-gray-700">
                    입력폼만 퍼가기
                  </label>
                </div>
              </div>
            ) : (
              <div className="!mb-4 !space-y-4">
                <div>
                  <div className="!flex !items-center !justify-between !mb-2">
                    <label className="!text-sm !font-medium !text-gray-700">가로폭(px)</label>
                  </div>
                  <div className="!flex !items-center !gap-2">
                    <input
                      type="number"
                      min="320"
                      placeholder="예) 600"
                      className="!flex-1 !p-2 !border !rounded"
                      value={buttonWidth}
                      onChange={(event) => setButtonWidth(event.target.value)}
                      onWheel={preventWheelChange}
                    />
                  </div>
                </div>

                <div>
                  <div className="!flex !items-center !justify-between !mb-2">
                    <label className="!text-sm !font-medium !text-gray-700">모달 위치</label>
                    <span className="!text-xs !text-gray-500">버튼은 오른쪽 하단에 고정되어 노출됩니다.</span>
                  </div>
                  <select
                    className="!w-full !p-2 !border !rounded"
                    value={buttonModalPosition}
                    onChange={(event) => setButtonModalPosition(event.target.value as 'left' | 'center' | 'right')}
                  >
                    <option value="left">왼쪽</option>
                    <option value="center">가운데</option>
                    <option value="right">오른쪽</option>
                  </select>
                </div>

                <div className="!flex !items-center !gap-2">
                  <input
                    id="buttonTypingToggle"
                    type="checkbox"
                    checked={buttonTypingEnabled}
                    onChange={(event) => setButtonTypingEnabled(event.target.checked)}
                  />
                  <label htmlFor="buttonTypingToggle" className="!text-sm !text-gray-700">
                    말풍선 타이핑 효과 표시
                  </label>
                </div>
              </div>
            )}

            {embedCodeToCopy ? (
              <>
                <textarea
                  readOnly
                  className="!w-full !h-24 !p-2 !border !rounded !bg-gray-50 !text-sm !resize-none !font-mono"
                  value={embedCodeToCopy}
                />
                <div className="!flex !justify-end !gap-3 !mt-4">
                  <button
                    className="!px-4 !py-2 !rounded !bg-gray-200 hover:!bg-gray-300"
                    onClick={resetEmbedModalState}
                  >
                    닫기
                  </button>
                  <button
                    className="!px-4 !py-2 !rounded !bg-blue-600 !text-white hover:!bg-blue-700"
                    onClick={handleCopyToClipboard}
                  >
                    복사
                  </button>
                </div>
              </>
            ) : (
              <div className="!flex !justify-end !gap-3 !mt-4">
                <button
                  className="!px-4 !py-2 !rounded !bg-gray-200 hover:!bg-gray-300"
                  onClick={resetEmbedModalState}
                >
                  닫기
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <CodeModal
        mode="create"
        isOpen={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />
      <CodeModal
        mode="edit"
        isOpen={isEditModalOpen && !!selectedCode}
        code={selectedCode ?? undefined}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedCode(null);
        }}
        onSuccess={async () => {
          const currentPage = codePage?.pageable?.pageNumber ?? 0;
          await fetchCodes(currentPage);
        }}
      />

      {/* 캠페인 삭제 확인 다이얼로그 */}
      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={onDeleteConfirm}
        title="캠페인 삭제 확인"
        message={`선택한 ${selectedCount}건의 캠페인을 삭제하시겠습니까?
삭제된 캠페인은 사용자 화면에서 더이상 보이지 않습니다.`}
        confirmText="삭제"
        cancelText="취소"
        type="danger"
      />
    </div>
  );
};

export default ConsultManagementPage;
